// Vulnerability advisories synced from OSV/GHSA.

import useSWR from 'swr';

export interface Advisory {
  id: string;
  source: 'osv' | 'ghsa' | 'manual';
  advisory_id: string;
  ecosystem: string;
  package_name: string;
  severity: '' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  summary: string;
  published_at?: string;
  modified_at?: string;
  fetched_at: string;
  match_count: number;
}

export interface AdvisoryMatch {
  id: string;
  path: string;
  version: string;
  repo_slug: string;
  format: string;
  project_slug: string;
}

export interface AdvisoryDetail extends Omit<Advisory, 'match_count'> {
  affected_ranges: unknown;
  references: unknown;
  matches: AdvisoryMatch[];
}

export interface AdvisoryLookupRow {
  id: string;
  source: string;
  advisory_id: string;
  ecosystem: string;
  package_name: string;
  severity: string;
  summary: string;
  matches: AdvisoryMatch[];
}

export interface SyncState {
  last_synced_at: string | null;
  last_count: number;
  last_error: string;
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useAdvisories(opts: { ecosystem?: string; severity?: string } = {}) {
  const params = new URLSearchParams();
  if (opts.ecosystem) params.set('ecosystem', opts.ecosystem);
  if (opts.severity) params.set('severity', opts.severity);
  const qs = params.toString();
  return useSWR<Advisory[]>(`/api/v1/advisories${qs ? '?' + qs : ''}`, fetcher);
}

export function useAdvisory(id: string | undefined) {
  return useSWR<AdvisoryDetail>(id ? `/api/v1/advisories/${id}` : null, fetcher);
}

export function useAdvisoryLookup(cve: string) {
  return useSWR<AdvisoryLookupRow[]>(
    cve ? `/api/v1/advisories/lookup?cve=${encodeURIComponent(cve)}` : null,
    fetcher,
  );
}

export function useSyncState() {
  return useSWR<SyncState>('/api/admin/advisories/sync_state', fetcher, {
    refreshInterval: 30_000,
  });
}

export async function triggerSync(): Promise<void> {
  const res = await fetch('/api/admin/advisories/sync', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 202) throw new Error(`sync failed: ${res.status}`);
}
